// M004-F0759 / F0760 (WebUI #205 / #206), coded to Spec core-ledger-account-gateway 1.18.0 (ML001-F0290): the customer statement
// operations, isolated in this one module. Same transport as invoiceApi.js and creditNoteApi.js: Core's Service Link Pass-through
// (CSLPT), POST /api/v1/orgs/:orgId/modules/ledger/messages with the closed envelope { relay_version: 1, type, payload, target_user_id }.
//
// Operations (Spec StatementRenderBody / StatementListBody, bodies forwarded unread to the Ledger):
//   statement.pdf    payload { customer_id, kind, as_at | from+to }   -> preview PDF, watermark PREVIEW, no number, not stored
//   statement.issue  payload { customer_id, kind, as_at | from+to }   -> Idempotency-Key; assigns the next <prefix>STA-<n>
//   statement.list   payload { customer_id, limit? }                  -> ISSUED statements only, newest first
//   statement.get    payload { customer_id, document_id? }           -> whole history, running balance_minor per line (M004-F0800)
// customer_id is a Spec EntityId STRING; target_user_id is the numeric Core user id (Core refuses a mismatch). Not user_id.
// Answers keep the typed shape: response.data.data = the Ledger's data (the same shape as creditNoteApi and invoiceApi).
import api from './api';
import { buildStatementBody, customerIdOf } from '../components/ledger/statementModel';

// M004-F0773 (WebUI #247): the Ledger has NEVER served statement.issue. It refuses it as NOT_YET_SERVED (Ledger ML001-D013 §2.77), so an
// Issue click gave users an error on prod. Until the Ledger serves it, the dialog does not offer Issue. Flip this to true only when the
// Ledger serves statement.issue (Ledger #188). A code constant, not an env var or build flag, so it is reviewed like any other change.
export const STATEMENT_ISSUE_SERVED = false;

const messagesPath = (orgId) => `/v1/orgs/${encodeURIComponent(orgId)}/modules/ledger/messages`;
const envelope = (type, payload, targetUserId) => ({ relay_version: 1, type, payload, target_user_id: targetUserId });
const keyed = (idempotencyKey) => ({ headers: { 'Idempotency-Key': idempotencyKey } });
const typedAnswer = (response) => ({
  ...response,
  data: { success: true, data: response?.data?.data?.payload?.data },
});

export const statementApi = {
  // Preview: never numbered, never stored. Builds the body through statementModel (fails closed on a bad date or customer).
  pdf: async (orgId, params) => {
    const body = buildStatementBody(params);
    return typedAnswer(await api.post(messagesPath(orgId), envelope('statement.pdf', body, Number(body.customer_id))));
  },
  // Issue: a write. Idempotency-Key is required so a retried click cannot take a second number.
  issue: async (orgId, params, idempotencyKey) => {
    if (!idempotencyKey) throw new Error('An idempotency key is required to issue a statement');
    const body = buildStatementBody(params);
    return typedAnswer(await api.post(messagesPath(orgId), envelope('statement.issue', body, Number(body.customer_id)), keyed(idempotencyKey)));
  },
  // List: issued statements of one customer, newest first.
  list: async (orgId, customerId, { limit } = {}) => {
    const id = customerIdOf(customerId);
    const payload = { customer_id: String(id), ...(limit === undefined ? {} : { limit }) };
    return typedAnswer(await api.post(messagesPath(orgId), envelope('statement.list', payload, id)));
  },
  // M004-F0800 (WebUI #266): the whole history of one customer, with the Ledger's running balance per line. Read only.
  // document_id is optional and passed through when given. Answer: { customer_id, lines: [...] } (see statementGetModel).
  getStatement: async (orgId, customerId, { documentId } = {}) => {
    const id = customerIdOf(customerId);
    const payload = { customer_id: String(id), ...(documentId === undefined ? {} : { document_id: documentId }) };
    return typedAnswer(await api.post(messagesPath(orgId), envelope('statement.get', payload, id)));
  },
};
